/* global gtag */

import React, { useState, useEffect } from "react";
import "./App.css";
import startSound from "./assets/sounds/start.mp3";
import correctSound from "./assets/sounds/correct.mp3";
import wrongSound from "./assets/sounds/wrong.mp3";
import gameOverSound from "./assets/sounds/game-over-sound.mp3";
import header from "./assets/images/header.png";
import share from "./assets/images/share.png";
import useAuthStore from "./store/useAuthStore";
import StartScreen from "./components/StartScreen";

const startAudio = new Audio(startSound);
const correctAudio = new Audio(correctSound);
const wrongAudio = new Audio(wrongSound);
const gameOverAudio = new Audio(gameOverSound);

function App() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [selected, setSelected] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(20);
  const [gameOver, setGameOver] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);
  const [hideOptions, setHideOptions] = useState(false);
  // New state arrays to track attempted and correct answers
  const [attemptedQuestions, setAttemptedQuestions] = useState([]);
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const { user, isAuthenticated, checkAuth } = useAuthStore();

  const gameId = window.location.pathname.split("/").filter(Boolean).pop();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    fetch("questions.json")
      .then((res) => res.json())
      .then((data) => {
        setQuestions(data.data); // No need to add attempted/correct properties
        setLoading(false);
        // Initialize tracking arrays
        setAttemptedQuestions(new Array(data.data.length).fill(false));
        setCorrectAnswers(new Array(data.data.length).fill(false));
      })
      .catch((err) => {
        console.error("Error loading questions:", err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (submitted || gameOver || !started || questions.length === 0) return;
    if (timeLeft === 0) {
      handleAutoSubmit();
      return;
    }
    const timer = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, submitted, gameOver, started, questions, currentQ]);

  const current = questions[currentQ];
  const isCurrentAttempted = attemptedQuestions[currentQ];
  const ArticleId = current?.url.split("/").pop();

  const handleStart = () => {
    if (typeof gtag === "function") {
      gtag("event", "start_clicked", {
        event_category: "User Actions",
        event_label: "Start",
        game_name: "headlines",
        userId: user?.user_id || "test",
        orgId: user?.org_id || "test",
        gameId: gameId,
        article_id: ArticleId,
        category: current?.category,
      });
    }
    startAudio.play();
    setStarted(true);
  };

  const handleAutoSubmit = () => {
    const newAttempted = [...attemptedQuestions];
    const newCorrect = [...correctAnswers];

    newAttempted[currentQ] = true;
    newCorrect[currentQ] = false;

    setAttemptedQuestions(newAttempted);
    setCorrectAnswers(newCorrect);
    wrongAudio.play();
    setSubmitted(true);
    if (typeof gtag === "function") {
      gtag("event", "auto_submit", {
        event_category: "User Actions",
        event_label: "Auto Submit",
        game_name: "headlines",
        userId: user?.user_id || "test",
        orgId: user?.org_id || "test",
        gameId: gameId,
        article_id: ArticleId,
        category: current?.category,
      });
    }
    setTimeout(() => {
      setHideOptions(true);
    }, 2000);
  };

  const handleSubmit = (selectedOption) => {
    if (submitted || attemptedQuestions[currentQ]) return;
    const isCorrect = selectedOption === questions[currentQ].correctAnswer;
    const newAttempted = [...attemptedQuestions];
    const newCorrect = [...correctAnswers];

    newAttempted[currentQ] = true;
    newCorrect[currentQ] = isCorrect;

    setCorrectAnswers(newCorrect);

    if (isCorrect) {
      correctAudio.play();
      setScore((s) => s + 1);
    } else {
      wrongAudio.play();
    }
    if (typeof gtag === "function") {
      gtag("event", "answer_submit", {
        event_category: "User Actions",
        event_label: "Answer Submit",
        answer: isCorrect ? "Right" : "Wrong",
        game_name: "headlines",
        userId: user?.user_id || "test",
        orgId: user?.org_id || "test",
        gameId: gameId,
        article_id: ArticleId,
        category: current?.category,
      });
    }
    setSelected(selectedOption);
    setSubmitted(true);
    setAttemptedQuestions(newAttempted);
    setTimeout(() => {
      setHideOptions(true);
    }, 2000);
  };

  const nextQuestion = () => {
    if (currentQ + 1 < questions.length) {
      setIsFlipping(true);
      gtag("event", "next_clicked", {
        event_category: "User Actions",
        event_label: "Next",
        score: `${score} / ${questions.length}`,
        game_name: "headlines",
        userId: user?.user_id || "test",
        orgId: user?.org_id || "test",
        gameId: gameId,
        article_id: ArticleId,
        category: current?.category,
      });
      setTimeout(() => {
        setCurrentQ((q) => q + 1);
        resetState();
        setIsFlipping(false);
      }, 600);
    } else {
      gtag("event", "finish_clicked", {
        event_category: "User Actions",
        event_label: "Finish",
        score: `${score} / ${questions.length}`,
        game_name: "headlines",
        userId: user?.user_id || "test",
        orgId: user?.org_id || "test",
        gameId: gameId,
        article_id: ArticleId,
        category: current?.category,
      });
      setGameOver(true);
      gameOverAudio.play();
    }
  };

  const previousQuestion = () => {
    if (currentQ > 0) {
      setCurrentQ(currentQ - 1);
      resetState();
    }
  };

  const resetState = () => {
    setSubmitted(false);
    setSelected("");
    setTimeLeft(20);
    setHideOptions(false);
  };
  const handleShare = () => {
    gtag("event", "share_clicked", {
      event_category: "User Actions",
      event_label: "Shared",
      score: `${score} / ${questions.length}`,
      game_name: "headlines",
      userId: user?.user_id || "test",
      orgId: user?.org_id || "test",
      gameId: gameId,
      article_id: ArticleId,
      category: current?.category,
    });
  };
  const handleRestart = () => {
    setAttemptedQuestions(new Array(questions.length).fill(false));
    setCorrectAnswers(new Array(questions.length).fill(false));
    setScore(0);
    setGameOver(false);
    setCurrentQ(0);
    setStarted(false);
    setSubmitted(false);
    setSelected("");
    setTimeLeft(20);
  };
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
        <h2>Loading...</h2>
      </div>
    );
  }

  if (gameOver) {
    const current = questions[currentQ];
    return (
      <div className="container">
        <div className="content-box">
          <div className="content-box-header">
            <img src={header} />
          </div>
          <div className="game-over-header">CONGRATULATIONS!</div>
          <div className="game-over-score">
            <div className="game-over-score-left">YOUR SCORE</div>
            <div className="game-over-score-right">
              {score} / {questions.length}
            </div>
          </div>
          <div className="game-over-box">
            <div className="game-over-text">
              30% of new players couldn’t answer this
            </div>
            <div className="game-over-share" onClick={handleShare}>
              <div>
                <img src={share} className="game-over-share-img" />
                SHARE
              </div>
            </div>
          </div>
          <a
            className="game-over-link"
            href={current.readMore}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              gtag("event", "readmore_clicked", {
                event_category: "User Actions",
                event_label: "Read More",
                link: current.readMore,
                game_name: "headlines",
                userId: user?.user_id || "test",
                orgId: user?.org_id || "test",
                gameId: gameId,
                article_id: ArticleId,
                category: current?.category,
              })
            }
          >
            Read Next Article
          </a>
        </div>
      </div>
    );
  }

  let displaySentence;

  if (submitted || isCurrentAttempted) {
    // After submit: show correct answer in the blank
    displaySentence = current.sentence.replace(
      /_{2,}/g,
      `<span id="blank" class="correct">${current.correctAnswer}</span>`
    );
  } else if (selected) {
    // Before submit: show selected answer in the blank
    displaySentence = current.sentence.replace(
      /_{2,}/g,
      `<span id="blank" class="user-selected">${selected}</span>`
    );
  } else {
    // Default: keep blank
    displaySentence = current.sentence;
  }

  if (!started) {
    const current = questions[currentQ];
    return (
      <StartScreen
        header={header}
        current={current}
        score={score}
        questions={questions}
        currentQ={currentQ}
        isFlipping={isFlipping}
        handleStart={handleStart}
      />
    );
  }
  return (
    <div className="container">
      <div className="content-box">
        <div className="content-box-header">
          <img src={header} />
        </div>
        <div className="headline-header">
          <div className="headline-header-left">{current.category}</div>
          <div className="headline-header-right">
            {new Date(current.publishedAt)
              .toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "numeric",
                year: "numeric",
              })
              .replace(/ /g, "-")}
          </div>
        </div>
        <div className="info-bar">
          <span className="info-bar-right">
            00:{timeLeft < 10 ? "0" : ""}
            {timeLeft}
          </span>
        </div>
        <div key={currentQ} className={`flipper ${isFlipping ? "flip" : ""}`}>
          <div className="content">
            <img
              src={current.image}
              alt={`question-${currentQ}`}
              style={{ width: "100%", height: "auto" }}
            />
            <div
              className="headline-box"
              // style={{ marginTop: hideOptions ? "-3rem" : "" }}
            >
              <p
                className="headline-text"
                dangerouslySetInnerHTML={{ __html: displaySentence }}
              />
              {!hideOptions && (
                <div className="options">
                  {current.options.map((option) => {
                    const isSelected = selected === option;
                    const isCorrect =
                      submitted && option === current.correctAnswer;
                    const isWrong = submitted && isSelected && !isCorrect;

                    let className = "option-btn";
                    if (submitted || isCurrentAttempted) {
                      if (isCorrect) className += " correct";
                      else if (isWrong) className += " wrong";
                    }

                    return (
                      <button
                        key={option}
                        className={className}
                        onClick={() => handleSubmit(option)}
                        disabled={
                          (submitted || isCurrentAttempted) &&
                          !isCorrect &&
                          !isWrong
                        }
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              )}
              {hideOptions && (
                <>
                  <div className="info-text">
                    <p>
                      {current.info}{" "}
                      <a
                        href={current.readMore}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          gtag("event", "readmore_clicked", {
                            event_category: "User Actions",
                            event_label: "Read More",
                            link: current.readMore,
                            game_name: "headlines",
                            userId: user?.user_id || "test",
                            orgId: user?.org_id || "test",
                            gameId: gameId,
                            article_id: ArticleId,
                            category: current?.category,
                          })
                        }
                      >
                        Read More
                      </a>
                    </p>
                  </div>
                  {/* <div className="nav-buttons">
                    <button onClick={nextQuestion}>
                      {currentQ === questions.length - 1 ? "Finish" : "Next"}
                    </button>
                  </div> */}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
